import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type {
  GetNarrative200,
  GetNarrativeType,
  PostNarrativeTriggerBodyType,
} from '@/api/generated/lucioAPI.schemas';
import {
  getNarrative,
  getNarrativeJobs,
  postNarrativeTrigger,
} from '@/api/generated/narrative/narrative';

export type RollingNarrativeKind = 'TODAY' | 'WEEK';

interface UseRollingNarrativesOptions {
  enabled?: boolean;
  kind: RollingNarrativeKind;
}

/**
 * Drives the lifecycle for a Rolling narrative window (TODAY/WEEK):
 * trigger a generation job on mount/kind-change, poll until it lands,
 * refetch the list. The backend dedupes in-flight jobs so re-triggering
 * on tab view is safe.
 */
export function useRollingNarratives({ enabled = true, kind }: UseRollingNarrativesOptions) {
  const queryClient = useQueryClient();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const listQuery = useInfiniteQuery<
    GetNarrative200,
    Error,
    { pageParams: Array<number>; pages: Array<GetNarrative200> },
    ReadonlyArray<unknown>,
    number
  >({
    enabled,
    gcTime: 1000 * 60 * 10,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) {
        return undefined;
      }
      const { page, totalPages } = pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
      getNarrative({
        limit: 30,
        page: pageParam,
        type: kind as GetNarrativeType,
      }),
    queryKey: ['narrative', 'list', kind],
    staleTime: 1000 * 60 * 5,
  });

  const jobsQuery = useQuery({
    enabled: enabled && activeJobId !== null,
    gcTime: 1000 * 60 * 5,
    queryFn: () => getNarrativeJobs(),
    queryKey: ['narrative', 'jobs'],
    refetchInterval: () => (activeJobId !== null ? 10_000 : false),
    staleTime: 0,
  });

  const triggerMutation = useMutation({
    mutationFn: () =>
      postNarrativeTrigger({
        type: kind as PostNarrativeTriggerBodyType,
      }),
    onSuccess: (response) => {
      const job = response;
      queryClient.invalidateQueries({ queryKey: ['narrative', 'jobs'] });
      if (!job) {
        return;
      }
      if (job.status === 'COMPLETED' || job.status === 'FAILED') {
        setActiveJobId(null);
        void listQuery.refetch();
      } else {
        setActiveJobId(job.id);
      }
    },
    retry: false,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- resets in-flight job marker before re-triggering on kind/enabled change
    setActiveJobId(null);
    triggerMutation.mutate();
    // oxlint-disable-next-line react-hooks-js/exhaustive-deps -- triggerMutation.mutate is stable
  }, [kind, enabled]);

  useEffect(() => {
    if (!activeJobId || !jobsQuery.data) {
      return;
    }
    const job = jobsQuery.data.find((j) => j.id === activeJobId);
    if (!job) {
      return;
    }
    if (job.status === 'COMPLETED' || job.status === 'FAILED') {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- syncs server job status into local in-flight marker
      setActiveJobId(null);
      void listQuery.refetch();
    }
  }, [activeJobId, jobsQuery.data, listQuery]);

  const narratives = listQuery.data?.pages.flatMap((page) => page.narratives ?? []) ?? [];

  return {
    isGenerating: activeJobId !== null,
    // Also report loading while the auto-trigger is in flight: the list may
    // resolve empty before we know whether a job is generating, and the page
    // would briefly flash the empty state otherwise.
    isLoading: listQuery.isLoading || triggerMutation.isPending,
    narratives,
    refetch: listQuery.refetch,
  };
}
